import React, { useState } from 'react';
import { useStoreState } from 'easy-peasy';
import HelperMetiers360 from "../../../../../services/HelpersMetiers360.js";
import PropTypes from 'prop-types';
import { Alert, Badge, Button, Form, InputGroup, Row, Col } from 'react-bootstrap';
import Loader from '../../../../widgets/Loader';
import Webapp2UserPDFTable from './Webapp2UserPDFTable.jsx';
import AuthorizationChecker from '../../../../../services/AuthorizationChecker.js';
import axios from '../../../../../services/axios';
import { useToast } from '../../../../../hooks/useToast.jsx';
import Webapp2UsersTable from './Webapp2UsersTable.jsx';
import SelectMultipleItemsWithSearchBar from '../../../../widgets/selectMultipleItemsWithSearchBar/SelectMultipleItemsWithSearchBar.jsx';

export const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    return Array.from({ length: 8 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
};

export const Webapp2ResetPasswordResult = ({ newPassword }) => {
    const [show, setShow] = useState(false);
    return <>
        <p>Voici le nouveau mot de passe.</p>
        <p>L'utilisateur·rice sera invité·e à changer son mot de passe lors de sa prochaine connexion.</p>
        <Form.Label className="fw-bold">Nouveau mot de passe</Form.Label>
        <InputGroup>
            <Form.Control
                type={show ? "text" : "password"}
                value={newPassword}
                readOnly
            />
            <Button variant="outline-secondary" onClick={() => setShow(s => !s)}>
                <i className={`fas fa-${show ? 'eye-slash' : 'eye'}`}></i>
            </Button>
        </InputGroup>
    </>;
};

const Webapp2UserEditForm = ({ user, csrfToken, onArchiveClick, onUnarchiveClick, onResetPasswordClick, onDeleteClick }) => {
    return (
        <Form id="webapp2UserEditForm" onSubmit={(event) => event.preventDefault()}>
            <Row>
                <Col>
                    <InputGroup>
                        <InputGroup.Text>Prénom :</InputGroup.Text>
                        <Form.Control
                            name="firstName"
                            defaultValue={user.firstName ?? ''}
                            placeholder='Ex : Prénom'
                            onInput={HelperMetiers360.toInputCamelcase}
                        />
                    </InputGroup>
                </Col>
                <Col>
                    <InputGroup>
                        <InputGroup.Text>Nom :</InputGroup.Text>
                        <Form.Control
                            name="lastName"
                            defaultValue={user.lastName ?? ''}
                            placeholder='[Majuscule] Ex : NOM'
                            onInput={HelperMetiers360.toInputUppercase}
                        />
                    </InputGroup>
                </Col>
            </Row>
            {/* We will implement that when we will add auth usage of webapp2 */}
            {/* <Row className="mt-2">
                <Col>
                    <InputGroup>
                        <InputGroup.Text>Email :</InputGroup.Text>
                        <Form.Control
                            type="email"
                            name="email"
                            defaultValue={user.email ?? ''}
                            placeholder='[Minuscule] Ex : adresse@email.com'
                            onInput={HelperMetiers360.toInputLowercase}
                        />
                    </InputGroup>
                </Col>
            </Row>*/}
            {(onArchiveClick || onUnarchiveClick || onResetPasswordClick || onDeleteClick) && <Row className="mt-5 justify-content-center">
                {onArchiveClick && <Col xs="auto">
                    <Button variant="warning" onClick={onArchiveClick}>
                        Archiver l'utilisateur·rice
                    </Button>
                </Col>}
                {onUnarchiveClick && <Col xs="auto">
                    <Button variant="warning" onClick={onUnarchiveClick}>
                        Désarchiver l'utilisateur·rice
                    </Button>
                </Col>}
                {onDeleteClick && <Col xs="auto">
                    <Button variant="danger" onClick={onDeleteClick}>
                        Supprimer l'utilisateur·rice
                    </Button>
                </Col>}
                {/* We will implement that when we will add auth usage of webapp2 */}
                {/* {onResetPasswordClick && <Col xs="auto">
                    <Button variant="warning" onClick={onResetPasswordClick}>
                        Réinitialiser le mot de passe
                    </Button>
                </Col>}*/}
            </Row>}
            <Form.Control name="csrf_token" defaultValue={csrfToken} type="hidden" />
        </Form>
    );
};

export const Webapp2UserGroupsForm = ({ user, allGroups }) => {
    const [selectedGroupIds, setSelectedGroupIds] = useState(
        user.webapp2Groups?.map(g => g.uniqueId) ?? []
    );

    return (
        <Form id="webapp2UserGroupsForm" onSubmit={(event) => event.preventDefault()}>
            <SelectMultipleItemsWithSearchBar
                idSelector="webapp2Groups[]"
                label="Groupes à associer"
                allItems={allGroups ?? []}
                selectedItemKeys={selectedGroupIds}
                setSelectedItemKeys={setSelectedGroupIds}
                itemKey="uniqueId"
                itemValue="name"
            />
            <input type="hidden" name="webapp2GroupsUpdated" value="1" />
        </Form>
    );
};

const Webapp2UsersStats = ({ setModalData, modalData, setModalShow, setIsSending, setUserStats, userStats, clientById, groups, setGroups, readOnly = false, displaySearchBar = true }) => {

    const { apiData } = useStoreState(state => state.actionSlugs);
    const toast = useToast();

    const openWebapp2UserModal = (uniqueId) => {
        const userStat = userStats?.find(s => s.uniqueId == uniqueId);
        setModalData({
            ...modalData,
            header: <>{getUserDisplayName(userStat)}</>,
            content: <Webapp2UserPDFTable uniqueId={uniqueId} userName={userStat?.userName} />,
            size: 'lg',
            id: 'webapp2-user-modal'
        });
        setModalShow(true);
    }

    const getUserDisplayName = (user) =>
        user.firstName || user.lastName
            ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim()
            : user.userName;

    const openResetPasswordResultModal = (userName, newPassword) => {
        setModalData(prev => ({
            ...prev,
            header: <>Réinitialiser le mot de passe de {userName}</>,
            content: <Webapp2ResetPasswordResult newPassword={newPassword} />,
            cancelButton: undefined,
            validateButton: 'Valider',
            onValidate: () => setModalShow(false),
            size: 'lg'
        }));
    };

    const openResetPasswordModal = (user) => {
        const userName = getUserDisplayName(user);
        const newPassword = generatePassword();

        const validate = () => {
            setIsSending(true);
            const resetUrl = apiData.ACTIONS?.webapp2User?.resetPassword?.url?.replace('uniqueId', user.uniqueId);
            const csrfToken = apiData.ACTIONS?.webapp2User?.resetPassword?.csrfToken;
            const formData = new FormData();
            formData.set('csrf_token', csrfToken);
            formData.set('password', newPassword);

            return axios.post(resetUrl, formData)
                .then(() => openResetPasswordResultModal(userName, newPassword))
                .catch((error) => Promise.reject(error))
                .finally(() => setIsSending(false));
        };

        setModalData(prev => ({
            ...prev,
            header: <>Réinitialiser le mot de passe de {userName}</>,
            content: <>
                <p>Êtes-vous sûr de vouloir <b>réinitialiser le mot de passe</b> de {userName} ?</p>
                <p>Un mot de passe automatique sera généré automatiquement.</p>
                <p>L'utilisateur·rice sera invité·e à changer son mot de passe lors de sa prochaine connexion.</p>
            </>,
            cancelButton: 'Annuler',
            validateButton: 'Valider',
            onValidate: validate,
            size: 'lg'
        }));
    };

    const openArchiveModal = (user) => {
        const userName = getUserDisplayName(user);

        const validate = () => {
            setIsSending(true);
            const archiveUrl = apiData.ACTIONS?.webapp2User?.archive?.url?.replace('uniqueId', user.uniqueId);
            const csrfToken = apiData.ACTIONS?.webapp2User?.archive?.csrfToken;
            const formData = new FormData();
            formData.set('csrf_token', csrfToken);

            return axios.post(archiveUrl, formData)
                .then((response) => {
                    setUserStats(prev => prev.map(u =>
                        u.uniqueId === user.uniqueId ? { ...u, ...response } : u
                    ));
                    toast.success();
                    setModalShow(false);
                })
                .catch((error) => Promise.reject(error))
                .finally(() => setIsSending(false));
        };

        setModalData(prev => ({
            ...prev,
            header: <>Archiver {userName}</>,
            content: <>
                <p>Êtes-vous sûr de vouloir <b>archiver</b> {userName} ?</p>
                <p>L'utilisateur·rice aura toujours un accès à la webapp.</p>
                <p>Si vous souhaitez que l'accès soit révoqué, vous devrez ensuite le ou la supprimer.</p>
            </>,
            cancelButton: 'Annuler',
            validateButton: 'Valider',
            onValidate: validate,
            size: 'lg'
        }));
    };

    const openUnarchiveModal = (user) => {
        const userName = getUserDisplayName(user);

        const validate = () => {
            setIsSending(true);
            const unarchiveUrl = apiData.ACTIONS?.webapp2User?.unarchive?.url?.replace('uniqueId', user.uniqueId);
            const csrfToken = apiData.ACTIONS?.webapp2User?.unarchive?.csrfToken;
            const formData = new FormData();
            formData.set('csrf_token', csrfToken);

            return axios.post(unarchiveUrl, formData)
                .then((response) => {
                    setUserStats(prev => prev.map(u =>
                        u.uniqueId === user.uniqueId ? { ...u, ...response } : u
                    ));
                    toast.success();
                    setModalShow(false);
                })
                .catch((error) => Promise.reject(error))
                .finally(() => setIsSending(false));
        };

        setModalData(prev => ({
            ...prev,
            header: <>Désarchiver {userName}</>,
            content: <>
                <p>Êtes-vous sûr de vouloir <b>désarchiver</b> {userName} ?</p>
            </>,
            cancelButton: 'Annuler',
            validateButton: 'Valider',
            onValidate: validate,
            size: 'lg'
        }));
    };

    const openDeleteModal = (user) => {
        const userName = getUserDisplayName(user);

        const validate = () => {
            setIsSending(true);
            const deleteUrl = apiData.ACTIONS?.webapp2User?.delete?.url?.replace('uniqueId', user.uniqueId);
            const csrfToken = apiData.ACTIONS?.webapp2User?.delete?.csrfToken;
            const formData = new FormData();
            formData.set('csrf_token', csrfToken);

            return axios.post(deleteUrl, formData)
                .then(() => {
                    const updatedStats = (userStats ?? []).filter(u => u.uniqueId !== user.uniqueId);
                    setUserStats(updatedStats);
                    if (setGroups) {
                        setGroups(prevGroups => prevGroups.map(g => ({
                            ...g,
                            webapp2UsersCount: updatedStats.filter(u =>
                                u.webapp2Groups?.some(ug => ug.uniqueId === g.uniqueId)
                            ).length
                        })));
                    }
                    toast.success();
                    setModalShow(false);
                })
                .catch((error) => Promise.reject(error))
                .finally(() => setIsSending(false));
        };

        setModalData(prev => ({
            ...prev,
            header: <>Supprimer {userName}</>,
            content: <>
                <p>Êtes-vous sûr de vouloir <b>supprimer</b> {userName} ?</p>
                <p>L'utilisateur·rice n'aura plus accès à son compte webapp et vous n'aurez plus accès aux informations de cet·te utilisateur·rice.</p>
                <p>Cette action est <b>irréversible</b>.</p>
            </>,
            cancelButton: 'Annuler',
            validateButton: 'Valider',
            onValidate: validate,
            size: 'lg'
        }));
        setModalShow(true);
    };

    const openEditModal = (user) => {
        const validate = () => {
            setIsSending(true);
            const formData = new FormData(document.getElementById('webapp2UserEditForm'));
            const updateUrl = apiData.ACTIONS?.webapp2User?.update?.url?.replace('uniqueId', user.uniqueId);

            return axios.post(updateUrl, formData)
                .then((response) => {
                    setUserStats(prev => prev.map(u => u.uniqueId === user.uniqueId
                        ? { ...u, ...response }
                        : u
                    ));
                    toast.success();
                    setModalShow(false);
                })
                .catch((error) => Promise.reject(error))
                .finally(() => setIsSending(false));
        };

        const csrfToken = apiData.ACTIONS?.webapp2User?.update?.csrfToken;
        const canUpdate = AuthorizationChecker.hasUpdateRights('webapp2User');

        setModalData(prev => ({
            ...prev,
            header: <>Modifier les informations de {getUserDisplayName(user)}</>,
            content: <Webapp2UserEditForm
                user={user}
                csrfToken={csrfToken}
                onArchiveClick={canUpdate && !user.isArchived && apiData.ACTIONS?.webapp2User?.archive
                    ? () => openArchiveModal(user)
                    : undefined}
                onUnarchiveClick={canUpdate && user.isArchived && apiData.ACTIONS?.webapp2User?.unarchive
                    ? () => openUnarchiveModal(user)
                    : undefined}
                onDeleteClick={canUpdate && user.isArchived && apiData.ACTIONS?.webapp2User?.delete
                    ? () => openDeleteModal(user)
                    : undefined}
                onResetPasswordClick={canUpdate && apiData.ACTIONS?.webapp2User?.resetPassword
                    ? () => openResetPasswordModal(user)
                    : undefined}
            />,
            cancelButton: 'Annuler',
            validateButton: 'Valider',
            onValidate: validate,
            size: 'lg'
        }));
        setModalShow(true);
    };

    const openGroupsModal = (user) => {
        const userName = getUserDisplayName(user);

        const validate = () => {
            setIsSending(true);
            const formData = new FormData(document.getElementById('webapp2UserGroupsForm'));
            const updateUrl = apiData.ACTIONS?.webapp2User?.update?.url?.replace('uniqueId', user.uniqueId);
            const csrfToken = apiData.ACTIONS?.webapp2User?.update?.csrfToken;
            formData.set('csrf_token', csrfToken);

            return axios.post(updateUrl, formData)
                .then((response) => {
                    const updatedStats = (userStats ?? []).map(u =>
                        u.uniqueId === user.uniqueId ? { ...u, ...response } : u
                    );
                    setUserStats(updatedStats);
                    if (setGroups) {
                        setGroups(prevGroups => prevGroups.map(g => ({
                            ...g,
                            webapp2UsersCount: updatedStats.filter(u =>
                                u.webapp2Groups?.some(ug => ug.uniqueId === g.uniqueId)
                            ).length
                        })));
                    }
                    toast.success();
                    setModalShow(false);
                })
                .catch((error) => Promise.reject(error))
                .finally(() => setIsSending(false));
        };

        setModalData(prev => ({
            ...prev,
            header: <>Modifier les groupes de {userName}</>,
            content: <Webapp2UserGroupsForm user={user} allGroups={groups} />,
            cancelButton: 'Annuler',
            validateButton: 'Valider',
            onValidate: validate,
            size: 'lg'
        }));
        setModalShow(true);
    };

    const handleClick = (val) => openWebapp2UserModal(val);

    return !userStats ?
        <Loader />
        : userStats?.length > 0 ?
            <Webapp2UsersTable
                users={userStats}
                groups={groups}
                handleClick={handleClick}
                valueInitSort="lastActivityAt"
                tableName="webapp2UsersStats"
                filename={`Webapp-utilisateurices-${HelperMetiers360.getdisplayDateType(new Date(), 'day')}`}
                showEditButton={!readOnly && AuthorizationChecker.hasUpdateRights('webapp2User')}
                showGroupsButton={!readOnly && AuthorizationChecker.hasUpdateRights('webapp2User') && groups?.length > 0}
                showLogsButton={true}
                onEditClick={openEditModal}
                onGroupsClick={openGroupsModal}
                displaySearchBar={displaySearchBar}
            />
            : <Alert variant='warning'>Pas de données disponibles.</Alert>;
};

Webapp2UsersStats.propTypes = {
    userStats: PropTypes.array,
    clientById: PropTypes.oneOfType([PropTypes.object, PropTypes.string]).isRequired,
    setUserStats: PropTypes.func,
    setIsSending: PropTypes.func,
    setModalData: PropTypes.func,
    modalData: PropTypes.object,
    setModalShow: PropTypes.func,
    groups: PropTypes.array,
    setGroups: PropTypes.func,
    readOnly: PropTypes.bool,
    displaySearchBar: PropTypes.bool,
};

export default Webapp2UsersStats;
